// frontend/src/components/Footer.js
//
// The single site-wide footer. Rendered ONLY by AuthLayout, so it appears on
// every AuthLayout-wrapped route: the homepage, the blog index + articles, the
// three SEO landings (business-website, facebook-group-scanning,
// supporter-network), the three legal
// pages and all the authenticated app routes. There is no second footer variant
// in the app - changing this file changes all of them at once.
//
// LAYOUT: three stacked rows at every width - a centred brand block, then the
// three titled link groups (product / support / legal) side by side, then a
// hairline and a copyright bar on its own. Phone differs only in type scale,
// not in structure. It replaced a single pipe-separated run of every link,
// which wrapped into an unreadable four-line blob on a phone.
//
// RTL: direction comes from AuthLayout's dir attribute alone. Every alignment
// here is LOGICAL ("start", grid track order) - do not add physical left/right
// overrides or the Hebrew layout ends up mirrored twice.
//
// SEO: the footer ships inside the prerendered HTML (scripts/prerender.js), so
// every entry must stay a real anchor. Only the cookie-settings entry is a
// <button> - it opens the consent dialog rather than navigating.
//
// LOOK - "Subtler B", the public footer's (HomeFooter) treatment, applied
// here too so the app and the public site end on the same band (brand
// rollout; navy audit round 3): a light navy tint (4.5%) over the page
// ground, navy text, and the brand wordmark + motto as the outlined brand
// files through components/brand/Logo.js - never typed text. Every text
// colour clears 4.5:1 on the tint, hover included.

import React from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { OPEN_SETTINGS_EVENT } from "./CookieConsent";
import Logo from "./brand/Logo";
import "../styles/footerBand.css";

// The band's tint, the ink on it (brand/tokens.json navy + the navy audit's
// ink-2 step, ~7.9:1 on the tint) and its top hairline - declared ONCE, for
// this footer and HomeFooter alike, in styles/footerBand.css.
const FOOTER_TINT = "var(--lf-foot-tint)";
const FOOTER_INK = "var(--lf-foot-ink)";
const FOOTER_INK_2 = "var(--lf-foot-ink-2)";
const FOOTER_HAIRLINE = "var(--lf-foot-hair)";

/** The brand block's rendered heights (px): the wordmark, then the motto line. */
export const APP_FOOTER_WORDMARK_HEIGHT = 26;
export const APP_FOOTER_MOTTO_HEIGHT = 14;

// The desktop gutter between the three link columns. A single grid `columnGap`,
// so both gutters are equal by construction at every width. MUI spacing units of
// 8px, so 25 = 200px; sm and xs step down from here because three columns have
// to share ~650px and ~358px respectively. 64px read huddled; 80, 112 and 144
// were each still too tight for the owner; 200px is his approved width
// (2026-08-10) - change only on his word.
const COLUMN_GAP = 25;

// Link columns are sized to their own content, with a floor. Equal FIXED
// widths were tried and rejected: the legal column's labels are ~80px shorter
// than the product column's, so a fixed width padded it with invisible space
// and threw the whole cluster ~45px off optical centre (measured on the
// rendered shot) even though the boxes were mathematically centred. Sizing to
// content makes the ink extents and the box extents the same thing.
const LINK_COLUMN_MIN = 112;

function Footer({ isHebrew }) {
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();

  // Translations
  const text = {
    blog: isHebrew ? "בלוג" : "Blog",
    supporterNetwork: isHebrew ? "רשת תומכים" : "Supporter Network",
    businessWebsite: isHebrew ? "בניית אתר לעסק" : "Business Website",
    facebookGroupScanning: isHebrew ? "סריקת קבוצות פייסבוק" : "Facebook Group Scanning",
    termsOfUse: isHebrew ? "תנאי שימוש" : "Terms of Use",
    privacyPolicy: isHebrew ? "מדיניות פרטיות" : "Privacy Policy",
    cookiePolicy: isHebrew ? "מדיניות עוגיות" : "Cookie Policy",
    cookieSettings: isHebrew ? "הגדרות עוגיות" : "Cookie Settings",
    accessibilityStatement: isHebrew ? "הצהרת נגישות" : "Accessibility Statement",
    contactUs: isHebrew ? "צור קשר" : "Contact Us",
    allRightsReserved: isHebrew ? "כל הזכויות שמורות" : "All rights reserved",
    // Group headings - the only new user-facing copy in this component.
    groupProduct: isHebrew ? "מוצר" : "Product",
    groupSupport: isHebrew ? "תמיכה" : "Support",
    groupLegal: isHebrew ? "משפטי" : "Legal",
    footerNav: isHebrew ? "ניווט בתחתית האתר" : "Footer navigation",
  };

  // One entry per column. `href` -> RouterLink anchor; `onClick` -> the single
  // action entry (re-opens the cookie consent dialog, the GDPR withdrawal
  // path that CookieConsentBanner listens for).
  // Item ORDER inside each column is owner-specified (2026-08-10): blog leads
  // the product column; cookie settings precede the accessibility statement;
  // the cookie policy precedes the privacy policy. The supporter-network landing
  // sits SECOND in the product column, directly under the blog (owner,
  // 2026-08-11) - it is not appended at the end.
  const linkGroups = [
    {
      id: "footer-group-product",
      title: text.groupProduct,
      items: [
        { href: "/blog", label: text.blog },
        { href: "/supporter-network", label: text.supporterNetwork },
        { href: "/business-website", label: text.businessWebsite },
        { href: "/facebook-group-scanning", label: text.facebookGroupScanning },
      ],
    },
    {
      id: "footer-group-support",
      title: text.groupSupport,
      items: [
        // Contact is offered to signed-in users only, exactly as before.
        ...(user ? [{ href: "/contact", label: text.contactUs }] : []),
        {
          onClick: () => window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT)),
          label: text.cookieSettings,
        },
        { href: "/accessibility", label: text.accessibilityStatement },
      ],
    },
    {
      id: "footer-group-legal",
      title: text.groupLegal,
      items: [
        { href: "/terms", label: text.termsOfUse },
        { href: "/cookies", label: text.cookiePolicy },
        { href: "/privacy", label: text.privacyPolicy },
      ],
    },
  ];

  // Unchanged from the pipe-row version, minus its `whiteSpace: nowrap`: inside
  // a narrow column a long label must be allowed to wrap rather than overflow.
  const linkSx = {
    color: FOOTER_INK_2,
    fontWeight: 500,
    // A step smaller on a phone: three columns share ~358px there, and the
    // long labels have to wrap inside their own column rather than push it.
    fontSize: { xs: "11.5px", sm: "12.5px", md: "13px" },
    lineHeight: 1.5,
    transition: "color 0.3s ease",
    display: "inline-flex",
    alignItems: "center",
    textAlign: "center",
    // Reset native <button> styles when used as an action link.
    background: "none",
    border: 0,
    p: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover, &:focus-visible": {
      color: FOOTER_INK,
      textDecoration: "underline",
      textUnderlineOffset: "3px",
    },
  };

  return (
    <Box
      component="footer"
      role="contentinfo"
      aria-label="Site footer"
      sx={{
        mt: "auto",
        py: { xs: 5, md: 6 },
        background: FOOTER_TINT,
        borderTop: `1px solid ${FOOTER_HAIRLINE}`,
      }}
    >
      <Container maxWidth="lg">
        {/* Three stacked ROWS at every width - brand, link columns, copyright.
            The brand is deliberately NOT a fourth column: keeping it on its own
            centred row leaves the link block symmetric, which is what lets it
            centre cleanly instead of being dragged off-axis by a brand column
            of a different width. */}
        <Box sx={{ mb: { xs: 3.5, md: 5 } }}>
          {/* Brand block - its own centred row above the columns: the
              outlined wordmark ("Leadify" navy, "AI" #4D7179) and the motto
              in outlined Alice (#3D6F7A), both images sized up front (no
              layout shift) and centred as blocks, so the page's reading
              direction cannot pull them apart. The motto stays English. */}
          <Box
            data-testid="app-footer-brand"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
              "& img": { display: "block", maxWidth: "100%", height: "auto" },
            }}
          >
            <Logo variant="wordmark" height={APP_FOOTER_WORDMARK_HEIGHT} />
            <Logo variant="motto" height={APP_FOOTER_MOTTO_HEIGHT} />
          </Box>
        </Box>

        {/* The three link groups - side by side at EVERY width, phone included.
            xs uses equal 1fr tracks so the three share the ~358px a 390px phone
            offers (a long label wraps inside its own column, which is fine -
            what is not fine is a column overflowing). From sm up the tracks are
            content-sized instead, so the cluster's ink extents and its box
            extents are the same thing and centring is optically true. */}
        <Box
          component="nav"
          aria-label={text.footerNav}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(3, minmax(0, 1fr))",
              sm: `repeat(3, minmax(${LINK_COLUMN_MIN}px, max-content))`,
            },
            justifyContent: "center",
            columnGap: { xs: 1.5, sm: 4, md: COLUMN_GAP },
            width: { xs: "100%", sm: "auto" },
            textAlign: "center",
            alignItems: "start",
          }}
        >
          {linkGroups.map((group) => (
            <Box key={group.id}>
              <Typography
                component="p"
                id={group.id}
                sx={{
                  color: FOOTER_INK_2,
                  fontWeight: 700,
                  fontSize: { xs: "11px", sm: "12.5px" },
                  lineHeight: 1.4,
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                {group.title}
              </Typography>

              <Box
                component="ul"
                aria-labelledby={group.id}
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // A link is ~20px tall; the row gap is what carries it past a
                  // comfortable tap target, so keep it wider on touch - where a
                  // wrapped two-line label also needs the extra separation.
                  gap: { xs: 1.5, sm: 1.25 },
                }}
              >
                {group.items.map((link) => (
                  <Box component="li" key={link.href || link.label}>
                    <Link
                      {...(link.href
                        ? { component: RouterLink, to: link.href }
                        : { component: "button", type: "button", onClick: link.onClick })}
                      underline="none"
                      sx={linkSx}
                      aria-label={link.label}
                    >
                      {link.label}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom bar - the copyright on its own, under a hairline. */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: { xs: 2.5, md: 3 },
            borderTop: `1px solid ${FOOTER_HAIRLINE}`,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: FOOTER_INK_2,
              fontSize: { xs: "12px", sm: "13px" },
              lineHeight: 1.5,
            }}
          >
            © {currentYear} LeadifyAI. {text.allRightsReserved}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
